import { ActorType, StepStatus, WorkflowBadgeStatus, type WorkflowComment, type WorkflowItem } from '#models';
import type { PersistedCaseDetail, PersistedCaseHeader, Uc2Assessment } from '../../../../models/persisted-case.model';
import type { Uc2Observation, Uc2SignalPayload, WellsExceptionPayload } from '../../../../models/persisted-case.model';
import type { Graph, Point, Series } from '#models';
import { UC2_PRESSURE_ROLE_LABELS, type Uc2SignalTimelineRow } from '../models/uc2-view-state';

export const UC2_ASSESSMENT_LABELS: Record<Uc2Assessment, string> = {
  LiftGasSupplyConcern: 'Lift-gas supply concern',
  BackpressureConcern: 'Backpressure concern',
  LocalWellPressureLoss: 'Local well pressure loss',
  PressureDisturbanceCauseUnclear: 'Pressure disturbance (cause unclear)',
};

export interface SignalTelemetryChart {
  graph: Graph;
}

export const WAT_DATE_FORMAT = 'dd MMM yyyy, HH:mm';

const METRIC_LABELS: Record<string, string> = {
  baselineMedian: 'Baseline pressure',
  baselineMad: 'Typical baseline variability',
  deviationMagnitude: 'Largest pressure change',
  magnitudeFloor: 'Minimum material pressure change',
  effectiveMagnitudeThreshold: 'Required pressure change',
  persistenceSamples: 'Longest sustained sequence',
  qualifyingSamples: 'Readings confirming the change',
  qualifyingEpisodeCount: 'Separate pressure episodes',
  usableWindowSamples: 'Usable readings in the analysis period',
  baselineWarmupSamples: 'Preceding baseline readings',
  expectedWindowSamplesFromObservedCadence: 'Expected readings at the observed frequency',
  observedCadenceMinutes: 'Typical time between readings',
  durationMinutes: 'Time from first to last qualifying reading',
  maximumAbsoluteModifiedZ: 'Largest deviation score',
  modifiedZThreshold: 'Required deviation score',
  minimumUsableWindowSamples: 'Minimum usable readings',
  minimumConsecutiveSamples: 'Required consecutive readings',
  robustSigmaMultiplier: 'Baseline variability scale factor',
  baselineLookbackHours: 'Baseline lookback period',
  minimumBaselineHalfCoverage: 'Required coverage in each half of the baseline',
  maximumBaselineIqr: 'Maximum allowed baseline spread',
  maximumBaselineHalfMedianDrift: 'Maximum allowed baseline drift',
  maximumGapCadenceMultiplier: 'Maximum gap relative to the normal reading interval',
  magnitudeFallbackPsi: 'Pressure-change threshold when instrument limits are unavailable',
};

export function humanizeLabel(value: string): string {
  const words = value.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[-_]/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function toMetricRows(metrics: Uc2SignalPayload['detectorMetrics']) {
  return metrics.map(metric => {
    const fraction = metric.units === 'fraction';
    const value = new Intl.NumberFormat('en-GB', { maximumSignificantDigits: 5 })
      .format(fraction ? metric.value * 100 : metric.value);
    const units = metric.units === 'samples' ? 'readings'
      : metric.units === 'sigma' ? 'standard deviations' : metric.units;
    const unitLabel = metric.value === 1 && units && ['readings', 'episodes', 'minutes', 'hours'].includes(units)
      ? units.slice(0, -1) : units;
    return {
      label: METRIC_LABELS[metric.name] ?? humanizeLabel(metric.name),
      value: `${value}${fraction ? '%' : unitLabel ? ` ${unitLabel}` : ''}`,
    };
  });
}

export function toQualityNotice(flag: string): string {
  const labels: Record<string, string> = {
    'bounded-pre-window-baseline-used': 'The baseline uses readings from the preceding analysis period.',
    'zero-robust-scale-materiality-fallback': 'The baseline was flat; the minimum pressure-change threshold was used.',
    'source-pressure-units-unreported': 'Pressure units were not reported by the source.',
    'right-censored-at-last-qualifying-sample': 'The change was still present at the last qualifying reading; recovery is not confirmed.',
    'well-state-unknown-or-ambiguous-at-production-day-grain': 'The daily operating record cannot establish the well state at the time of the event.',
    'pi-band-untrusted-or-missing': 'Verified instrument operating limits are unavailable.',
    'magnitude-fallback-used': 'The minimum pressure-change threshold was used because verified instrument limits are unavailable.',
    'telemetry-only-provisional-baseline': 'The baseline is estimated from telemetry and has not been independently verified.',
  };
  return labels[flag] ?? humanizeLabel(flag);
}

export function toPersistedCaseTitle(title: string): string {
  for (const [assessment, label] of Object.entries(UC2_ASSESSMENT_LABELS)) {
    if (title.startsWith(`${assessment}:`)) {
      return `${label}:${title.slice(assessment.length + 1)}`;
    }
  }
  return title;
}

export function toSignalLabel(signal: Uc2SignalPayload): string {
  return `${signal.context.well_code}: ${UC2_PRESSURE_ROLE_LABELS[signal.context.pressure_role]} pressure ${signal.context.concern_direction}`;
}

function toPriorityName(priorityId: number | null | undefined): string | null {
  return priorityId === 3 ? 'High' : priorityId === 2 ? 'Medium' : priorityId === 1 ? 'Low' : null;
}

function toWorkflowComments(comments: PersistedCaseDetail['comments']): WorkflowComment[] {
  return comments?.map(comment => ({
    id: String(comment.agentCaseCommentId), text: comment.commentText,
    author: comment.createdBy, authorEmail: comment.createdByEmail, createdAt: comment.createdDate,
  })) ?? [];
}

export function toPersistedCaseItem(header: PersistedCaseHeader, detail?: PersistedCaseDetail): WorkflowItem {
  if (header.useCase.toUpperCase() !== 'UC2' && header.caseType !== 'well-exception') {
    const payload = detail?.detailJson;
    const rca = payload && 'workflowSteps' in payload ? payload : undefined;
    const uc1Candidates = payload && 'candidates' in payload ? payload.candidates : undefined;
    const uc1Evidence = payload && 'candidates' in payload ? payload.evidence : undefined;
    const uc1SummaryEvidenceIds = payload && 'candidates' in payload ? payload.summaryEvidenceIds : undefined;
    return {
      id: `case-${header.agentCaseId}`,
      source: 'workflow',
      title: header.title,
      badge: header.frozenAt ? WorkflowBadgeStatus.Closed : WorkflowBadgeStatus.New,
      asset: header.asset,
      priority: header.priorityName ?? toPriorityName(header.priorityId ?? rca?.priorityId),
      runForDate: header.runForDate,
      confidenceScore: header.confidenceScore ?? rca?.confidenceLevel ?? null,
      confidenceBand: header.confidenceBand,
      description: header.description,
      summary: header.summary,
      actionText: header.recommendedAction ?? undefined,
      createdDate: header.createdDate,
      runCompletedAt: header.runCompletedAt,
      sweepTimeUtc: header.sweepTimeUtc,
      updatedDate: header.updatedDate,
      dataForDate: header.dataForDate,
      displayReport: header.displayReport,
      exceptionReferenceId: header.engineCaseNumber ?? null,
      status: header.frozenAt ? 'Closed' : 'New',
      linkedEngineCaseId: header.engineCaseId ?? null,
      ...(uc1Candidates !== undefined ? { uc1Candidates } : {}),
      ...(uc1Evidence !== undefined ? { uc1Evidence } : {}),
      ...(uc1SummaryEvidenceIds !== undefined ? { uc1SummaryEvidenceIds } : {}),
      identity: {
        title: header.title,
        area: header.areaName ?? rca?.well?.area ?? '',
        supplementary: header.supplementaryContext ?? rca?.well?.supplementary ?? '',
        stream: header.streamName ?? rca?.well?.stream ?? '',
        product: header.productName ?? rca?.well?.product ?? '',
        exceptionType: header.exceptionTypeName ?? rca?.exceptionType ?? 'Export Volume Deviation',
        useCase: header.useCase,
        confidenceLevel: header.confidenceScore ?? rca?.confidenceLevel ?? 0,
        confidenceStatement: '',
      },
      steps: rca?.workflowSteps?.map(step => ({
        number: step.stepNumber,
        title: step.title,
        actor: step.actorType?.toLowerCase() === 'ai agent' ? ActorType.AIAgent : ActorType.Human,
        status: step.completedAt ? StepStatus.Done : StepStatus.Pending,
        sub: step.summary ?? '',
        completedOn: step.completedAt ?? null,
        completedBy: step.completedBy ?? null,
        source: step.source ?? null,
        summary: step.summary ?? null,
        evidenceDataJson: step.blocks ? JSON.stringify(step.blocks) : null,
      })) ?? [],
      comments: toWorkflowComments(detail?.comments),
    };
  }
  return {
    id: `case-${header.agentCaseId ?? header.caseId}`,
    source: 'uc2-case',
    persistedCase: header,
    casePayload: (detail?.detailJson ?? detail?.payload) as WellsExceptionPayload | undefined,
    title: toPersistedCaseTitle(header.title),
    badge: WorkflowBadgeStatus.Proposed,
    asset: header.asset,
    priority: header.priorityName ?? toPriorityName(header.priorityId),
    runForDate: header.runForDate,
    confidenceScore: header.confidenceScore ?? null,
    confidenceBand: header.confidenceBand,
    description: header.description,
    summary: header.summary,
    createdDate: header.createdDate,
    runCompletedAt: header.runCompletedAt,
    updatedDate: header.updatedDate,
    dataForDate: header.dataForDate,
    displayReport: header.displayReport,
    exceptionReferenceId: '',
    status: 'Well Exception',
    linkedEngineCaseId: header.engineCaseId ?? null,
    steps: [],
    comments: toWorkflowComments(detail?.comments),
  };
}

export function toSignalTelemetry(
  signal: Uc2SignalPayload,
  citations: WellsExceptionPayload['dossier']['citations'],
  seriesColor: string,
) {
  const supportingIdsByRole = new Map<string, Set<string>>();
  for (const related of signal.corroboratingSignals) {
    const ids = supportingIdsByRole.get(related.role) ?? new Set<string>();
    ids.add(related.signalId);
    supportingIdsByRole.set(related.role, ids);
  }
  const ambiguousIds = new Set([...supportingIdsByRole.values()].filter(ids => ids.size > 1).flatMap(ids => [...ids]));
  const charts: SignalTelemetryChart[] = signal.evidence.flatMap(evidence => 'html' in evidence ? [] : [{
    graph: {
      graphType: 'timeGraph',
      title: evidence.title,
      xAxis: { label: evidence.xAxis.label, uom: evidence.xAxis.uom ?? '' },
      yAxis: { label: evidence.yAxis.label, uom: evidence.yAxis.uom ?? '' },
      series: evidence.series.map(series => ({ ...series, data: [...series.data].sort((a, b) => a.x - b.x) })),
    },
  }]);
  const emptySources: string[] = [];

  function addSeries(title: string, units: string, series: Series): void {
    if (!series.data.length) {
      emptySources.push(`${title}: no readings were supplied.`);
      return;
    }
    series.data.sort((a, b) => a.x - b.x);
    // Reuse an exact charted series; repeated copies in citations/history must not create duplicate charts.
    const existing = charts.find(chart => chart.graph.yAxis.uom === units && chart.graph.series.some(candidate =>
      candidate.id === series.id && candidate.data.length === series.data.length && candidate.data.every((point, index) =>
        point.x === series.data[index].x && point.y === series.data[index].y)));
    if (existing) {
      return;
    }
    charts.push({
      graph: {
        graphType: 'timeGraph', title,
        xAxis: { label: 'Observation time', uom: '' },
        yAxis: { label: 'Value', uom: units },
        series: [{ ...series, color: seriesColor }],
      },
    });
  }

  const measurements = new Map<string, { measure: string; units: string; data: Point[] }>();
  for (const measurement of [...signal.historicalValues, signal.latestValue]) {
    const key = JSON.stringify([measurement.measure, measurement.uom]);
    let series = measurements.get(key);
    if (!series) {
      series = { measure: measurement.measure, units: measurement.uom, data: [] };
      measurements.set(key, series);
    }
    const point = { x: Date.parse(measurement.timestamp), y: measurement.value };
    if (!series.data.some(existing => existing.x === point.x && existing.y === point.y)) {
      series.data.push(point);
    }
  }
  for (const [key, measurement] of measurements) {
    const title = `${signal.context.well_code}: ${measurement.measure}`;
    const id = measurement.measure === signal.latestValue.measure && measurement.units === signal.latestValue.uom
      ? signal.signalId : `${signal.signalId}:${key}`;
    addSeries(title, measurement.units, { id, name: title, data: measurement.data });
  }

  function addObservations(title: string, id: string, observations: Uc2Observation[], fallbackUnits: string): void {
    const groups = new Map<string, Point[]>();
    for (const observation of observations) {
      const units = observation.units ?? fallbackUnits;
      const data = groups.get(units) ?? [];
      data.push({ x: Date.parse(observation.observedAt), y: observation.value });
      groups.set(units, data);
    }
    if (!groups.size) {
      const sourceTitle = ambiguousIds.has(id) ? `${title} (${id})` : title;
      emptySources.push(`${sourceTitle}: no readings were supplied.`);
    }
    for (const [units, data] of groups) {
      addSeries(title, units, { id, name: title, data });
    }
  }

  for (const related of signal.corroboratingSignals) {
    addObservations(`${signal.context.well_code}: supporting ${related.role.replace(/-/g, ' ')}`,
      related.signalId, related.observations, related.units);
  }
  for (const citation of citations.filter(citation => signal.citationIds.includes(citation.citationId)
    || citation.signalIds.includes(signal.signalId))) {
    addObservations(toSignalLabel(signal), signal.signalId, citation.observations, signal.metrics.units);
    addObservations(`${signal.context.well_code}: preceding comparison history`, `${signal.signalId}-baseline`, citation.baselineObservations,
      signal.metrics.units);
  }

  for (const chart of charts) {
    const ids = [...new Set(chart.graph.series.filter(series => ambiguousIds.has(series.id)).map(series => series.id))];
    if (ids.length) {
      chart.graph.title += ` (${ids.join(', ')})`;
      chart.graph.series = chart.graph.series.map(series => ({
        ...series, name: ambiguousIds.has(series.id) ? `${series.name} (${series.id})` : series.name,
      }));
    }
  }

  return { signal, label: toSignalLabel(signal), charts, emptySources: [...new Set(emptySources)] };
}

export function toTelemetryOverlays(
  entries: ReturnType<typeof toSignalTelemetry>[],
  colors: string[],
): { title: string; graphs: Graph[] }[] {
  const categories = [
    { title: 'Pressure', units: ['psi', 'psig', 'psia', 'bar', 'barg', 'bara', 'kpa', 'mpa', 'pa'] },
    { title: 'Temperature', units: ['c', 'f', 'k', 'degc', 'degf', '\u00b0c', '\u00b0f', 'celsius', 'fahrenheit'] },
  ];
  return categories.map(category => {
    const graphs = new Map<string, Graph>();
    const sensors = new Map<string, Series>();
    for (const entry of entries) {
      for (const { graph } of entry.charts) {
        const units = graph.yAxis.uom.trim().toLowerCase();
        for (const source of graph.series) {
          const role = entry.signal.corroboratingSignals.find(related => related.signalId === source.id)?.role;
          const reportedTemperature = ['source-reported-temperature', 'source-unreported'].includes(units)
            && role === 'temperature';
          const unreportedPressure = ['', 'source-unreported', 'source-pressure-units-unreported'].includes(units)
            && (source.id === entry.signal.signalId || source.id === `${entry.signal.signalId}-baseline` || role === 'pressure');
          if (!category.units.includes(units)
            && !(category.title === 'Temperature' && reportedTemperature)
            && !(category.title === 'Pressure' && unreportedPressure)) continue;
          const data = source.data.filter(point => Number.isFinite(point.x) && Number.isFinite(point.y));
          if (!data.length) continue;
          const sensorId = source.id === `${entry.signal.signalId}-baseline` ? entry.signal.signalId : source.id;
          const groupUnits = unreportedPressure ? 'Units unreported' : units;
          const key = JSON.stringify([entry.signal.context.well_code, sensorId, groupUnits]);
          let series = sensors.get(key);
          if (!series) {
            let target = graphs.get(groupUnits);
            if (!target) {
              target = {
                graphType: 'timeGraph', title: category.title,
                xAxis: { label: 'Observation time', uom: '' },
                yAxis: { label: category.title, uom: unreportedPressure ? groupUnits : graph.yAxis.uom }, series: [],
              };
              graphs.set(groupUnits, target);
            }
            const name = sensorId === entry.signal.signalId
              ? `${entry.signal.context.well_code}: ${UC2_PRESSURE_ROLE_LABELS[entry.signal.context.pressure_role]}`
              : `${source.name} (${sensorId})`;
            series = { id: key, name, color: colors[sensors.size % colors.length], data: [] };
            sensors.set(key, series);
            target.series.push(series);
          }
          for (const point of data) {
            if (!series.data.some(existing => existing.x === point.x && existing.y === point.y)) {
              series.data.push({ ...point });
            }
          }
        }
      }
    }
    for (const series of sensors.values()) series.data.sort((left, right) => left.x - right.x);
    return { title: category.title, graphs: [...graphs.values()] };
  });
}

export function toSignalTimeline(signals: Uc2SignalPayload[]): Uc2SignalTimelineRow[] {
  return signals.map((signal, index) => ({
    exceptionId: String(index),
    asset: `${signal.context.well_code} / ${UC2_PRESSURE_ROLE_LABELS[signal.context.pressure_role]}`,
    signalClass: signal.signalDescription,
    startMs: Date.parse(signal.episodeOnsetUtc),
    endMs: Math.max(...signal.episodes.map(episode => Date.parse(episode.lastQualifyingAt))),
    intervals: signal.episodes.map(episode => [Date.parse(episode.onset), Date.parse(episode.lastQualifyingAt)] as [number, number]),
    detectedMs: Date.parse(signal.detectedAt),
    context: [
      { label: 'Measurement', value: UC2_PRESSURE_ROLE_LABELS[signal.context.pressure_role] },
      { label: 'Direction', value: humanizeLabel(signal.context.concern_direction) },
    ],
  })).sort((left, right) => left.startMs - right.startMs || left.asset.localeCompare(right.asset));
}
